/**
 * `/admin/compliance-obligations` — admin configuration surface for the
 * compliance requirements the register tracks (M7 API, M13 UI, M10 rework).
 * The SPA URL was renamed to "obligations" as an admin-facing copy
 * change; the backend API path and DB table stay as
 * `compliance_requirements`.
 *
 * Every authenticated admin can list, add, and edit obligations (the
 * role-tier model was retired in M15) — the same API gate that backs
 * every /admin page.
 *
 * Structure mirrors `/admin/users`: breadcrumb → intro → inline "Add
 * requirement" disclosure → filter toggle → paginated list. Every row
 * links to `/admin/compliance-obligations/:requirementId` for the full
 * edit form.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../../api/client.js';
import type { ApiClient } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import type { ComplianceRequirement, RequirementPattern } from '@isms/shared';

export interface ComplianceRequirementsListPageProps {
  client: ApiClient;
  /** Where to send the admin on 401. Defaults to `/admin/sign-in`. */
  signInPath?: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; error: ApiError | string }
  | { kind: 'ready'; items: ComplianceRequirement[] };

type CreateStatus =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'error'; error: ApiError | string };

type ActiveFilter = 'all' | 'active' | 'inactive';

const PATTERN_LABELS: Record<RequirementPattern, string> = {
  upload: 'Upload',
  sign: 'Sign',
  admin_attestation: 'Admin attestation',
  volunteer_attestation: 'Volunteer attestation',
};

export function ComplianceRequirementsListPage({
  client,
  signInPath = '/admin/sign-in',
}: ComplianceRequirementsListPageProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [filter, setFilter] = useState<ActiveFilter>('all');
  const [showCreate, setShowCreate] = useState(false);
  const [createStatus, setCreateStatus] = useState<CreateStatus>({ kind: 'idle' });
  const navigate = useNavigate();

  const redirectToSignIn = useCallback(() => {
    navigate(signInPath, { replace: true });
  }, [navigate, signInPath]);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const res = await client.adminListComplianceRequirements();
      setState({ kind: 'ready', items: res.items });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        redirectToSignIn();
        return;
      }
      setState({
        kind: 'error',
        error:
          err instanceof ApiError
            ? err
            : "We couldn't load the obligations list. Please check your connection and try again.",
      });
    }
  }, [client, redirectToSignIn]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredItems = useMemo(() => {
    if (state.kind !== 'ready') return [];
    if (filter === 'all') return state.items;
    const wantActive = filter === 'active';
    return state.items.filter((it) => it.active === wantActive);
  }, [state, filter]);

  const handleCreate = useCallback(
    async (fields: CreateFormFields) => {
      setCreateStatus({ kind: 'pending' });
      try {
        const created = await client.adminCreateComplianceRequirement(buildCreateRequest(fields));
        navigate(
          `/admin/compliance-obligations/${encodeURIComponent(String(created.requirement_id))}`,
        );
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          redirectToSignIn();
          return;
        }
        setCreateStatus({
          kind: 'error',
          error:
            err instanceof ApiError ? err : "We couldn't create that obligation. Please try again.",
        });
      }
    },
    [client, navigate, redirectToSignIn],
  );

  return (
    <main className="admin-item-types" aria-labelledby="admin-item-types-heading">
      <nav className="admin-people-breadcrumb" aria-label="Breadcrumb">
        <Link to="/admin">← Back to dashboard</Link>
      </nav>

      <header className="admin-users-header">
        <h1 id="admin-item-types-heading">Compliance obligations</h1>
        <p className="admin-users-intro">
          Configure the obligations the register tracks. Deactivating an obligation hides it from
          future intake and <code>/me</code> without touching historical records.
        </p>
      </header>

      <section className="admin-users-create" aria-labelledby="admin-item-types-create-heading">
        <header className="admin-users-create-header">
          <h2 id="admin-item-types-create-heading">Add obligation</h2>
          <button
            type="button"
            className="admin-users-create-toggle"
            onClick={() => {
              setShowCreate((v) => !v);
              setCreateStatus({ kind: 'idle' });
            }}
            aria-expanded={showCreate}
          >
            {showCreate ? 'Cancel' : 'Add obligation'}
          </button>
        </header>
        {showCreate ? (
          <CreateRequirementForm
            status={createStatus}
            onSubmit={(fields) => void handleCreate(fields)}
          />
        ) : (
          <p className="admin-users-create-hint">
            Open the form to add a new obligation. You can tweak the pattern, copy, and custom
            fields later from the detail screen.
          </p>
        )}
      </section>

      <section
        className="admin-item-types-list-section"
        aria-labelledby="admin-item-types-list-heading"
      >
        <header className="admin-users-list-header">
          <h2 id="admin-item-types-list-heading">Obligations</h2>
          <div className="admin-item-types-filter" role="radiogroup" aria-label="Active filter">
            {(['all', 'active', 'inactive'] as ActiveFilter[]).map((v) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={filter === v}
                className={`admin-people-filter-tab${filter === v ? ' is-active' : ''}`}
                onClick={() => setFilter(v)}
              >
                {v === 'all' ? 'All' : v === 'active' ? 'Active only' : 'Inactive only'}
              </button>
            ))}
          </div>
        </header>

        {state.kind === 'loading' ? (
          <p className="admin-people-loading" aria-busy="true">
            Loading obligations…
          </p>
        ) : null}

        {state.kind === 'error' ? (
          <div className="admin-people-error">
            <ErrorBanner error={state.error} />
            <button type="button" onClick={() => void load()}>
              Retry
            </button>
          </div>
        ) : null}

        {state.kind === 'ready' && filteredItems.length === 0 ? (
          <p className="admin-people-empty">
            {filter === 'all'
              ? 'No obligations configured yet. Use the form above to add the first one.'
              : `No ${filter} obligations. Switch the filter to see the others.`}
          </p>
        ) : null}

        {state.kind === 'ready' && filteredItems.length > 0 ? (
          <ul className="admin-users-list">
            {filteredItems.map((item) => (
              <RequirementRow key={String(item.requirement_id)} item={item} />
            ))}
          </ul>
        ) : null}
      </section>
    </main>
  );
}

interface RequirementRowProps {
  item: ComplianceRequirement;
}

function RequirementRow({ item }: RequirementRowProps): JSX.Element {
  const id = String(item.requirement_id);
  return (
    <li className="admin-users-row">
      <Link
        to={`/admin/compliance-obligations/${encodeURIComponent(id)}`}
        className="admin-users-row-link"
      >
        <span className="admin-users-row-name">{item.name}</span>
        <span className="admin-users-row-email">
          <code>{item.short_code}</code>
        </span>
        <span className="admin-users-row-meta">
          <span className="admin-users-role-chip">Pattern: {PATTERN_LABELS[item.pattern]}</span>
          <span
            className={`admin-people-status ${
              item.active ? 'admin-people-status-active' : 'admin-people-status-inactive'
            }`}
          >
            {item.active ? 'Active' : 'Inactive'}
          </span>
        </span>
      </Link>
    </li>
  );
}

interface CreateFormFields {
  name: string;
  shortCode: string;
  pattern: RequirementPattern;
  active: boolean;
}

interface CreateRequirementFormProps {
  status: CreateStatus;
  onSubmit: (fields: CreateFormFields) => void;
}

function CreateRequirementForm({ status, onSubmit }: CreateRequirementFormProps): JSX.Element {
  const [name, setName] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [pattern, setPattern] = useState<RequirementPattern>('upload');
  const [active, setActive] = useState(true);

  const pending = status.kind === 'pending';

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (pending) return;
    const trimmedName = name.trim();
    const trimmedCode = shortCode.trim();
    if (!trimmedName || !trimmedCode) return;
    onSubmit({
      name: trimmedName,
      shortCode: trimmedCode,
      pattern,
      active,
    });
  }

  return (
    <form className="admin-users-create-form" onSubmit={handleSubmit} noValidate>
      <div className="admin-users-form-row">
        <label htmlFor="admin-item-types-create-name">Name</label>
        <input
          id="admin-item-types-create-name"
          type="text"
          autoComplete="off"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={pending}
        />
      </div>
      <div className="admin-users-form-row">
        <label htmlFor="admin-item-types-create-short-code">Short code</label>
        <input
          id="admin-item-types-create-short-code"
          type="text"
          autoComplete="off"
          required
          value={shortCode}
          onChange={(e) => setShortCode(e.target.value)}
          disabled={pending}
        />
        <p className="admin-users-form-hint">
          Stable identifier used by worker jobs and seed data (e.g. <code>WWCC</code>,{' '}
          <code>FOUNDATION</code>). Keep it short and uppercase.
        </p>
      </div>
      <div className="admin-users-form-row">
        <label htmlFor="admin-item-types-create-pattern">Pattern</label>
        <select
          id="admin-item-types-create-pattern"
          value={pattern}
          onChange={(e) => setPattern(e.target.value as RequirementPattern)}
          disabled={pending}
        >
          <option value="upload">Upload</option>
          <option value="sign">Sign</option>
          <option value="admin_attestation">Admin attestation</option>
          <option value="volunteer_attestation">Volunteer attestation</option>
        </select>
      </div>
      <div className="admin-users-form-row admin-users-form-row-checkbox">
        <label htmlFor="admin-item-types-create-active">
          <input
            id="admin-item-types-create-active"
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            disabled={pending}
          />
          Active — new volunteers will be required to supply this
        </label>
      </div>
      <div className="admin-users-create-actions">
        <button type="submit" className="admin-exports-primary-button" disabled={pending}>
          {pending ? 'Creating…' : 'Create requirement'}
        </button>
      </div>
      {status.kind === 'error' ? <ErrorBanner error={status.error} /> : null}
    </form>
  );
}

function buildCreateRequest(fields: CreateFormFields) {
  // Start the new requirement with sensible defaults — the admin can tune
  // the markdown, expiry, and linked document on the detail screen.
  return {
    name: fields.name,
    short_code: fields.shortCode,
    pattern: fields.pattern,
    active: fields.active,
    markdown_explainer: '',
    linked_document_id: null,
    expiry_duration_days: null,
  };
}
